import {VersionName} from '@github-ui/copilot-chat/components/VersionName'
import {ActionList, ActionMenu} from '@primer/react'
import {useEffect} from 'react'

import {type PreviewableContent, stripVersionFromId} from './content-preview-types'
import {useContentPreview} from './ContentPreviewContext'
import styles from './VersionSelector.module.css'

export interface VersionSelectorProps {
  item: PreviewableContent
  onVersionSelect?: (versionName: string) => void
}

export function VersionSelector({item, onVersionSelect}: VersionSelectorProps) {
  const {versionedItems, openItem, items} = useContentPreview()

  const unversionedId = stripVersionFromId(item.id)
  const versions = versionedItems.get(unversionedId) ?? []

  // Resolve duplicates: if this item is marked as a duplicate, use the original item's ID
  let itemIdForVersion = item.id
  if ('duplicateOf' in item && item.duplicateOf) {
    itemIdForVersion = item.duplicateOf
  }

  const currentVersionIndex = versions.indexOf(itemIdForVersion)

  useEffect(() => {
    if (onVersionSelect && versions.length > 1) {
      onVersionSelect(`Version${currentVersionIndex + 1}`)
    }
  }, [currentVersionIndex, onVersionSelect, versions.length])

  // Hide version selector while ANY item is streaming to prevent strobing during message streaming
  const isAnyItemStreaming = Array.from(items.values()).some(i => 'isStreaming' in i && i.isStreaming)
  if (versions.length <= 1 || isAnyItemStreaming) return null

  return (
    <ActionMenu>
      <ActionMenu.Button variant="invisible" className={styles.anchor}>
        <VersionName version={currentVersionIndex + 1} data-testid="version-selector" />
      </ActionMenu.Button>

      <ActionMenu.Overlay>
        <ActionList selectionVariant="single">
          {versions.map((versionId, i) => (
            <ActionList.Item
              key={versionId}
              onSelect={() => {
                openItem(versionId)
                if (onVersionSelect) {
                  onVersionSelect(`Version${i + 1}`)
                }
              }}
              selected={versionId === item.id}
            >
              <ActionList.LeadingVisual>
                <VersionName version={i + 1} />
              </ActionList.LeadingVisual>
              Version {i + 1}
            </ActionList.Item>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
